/**
 * Mobile ESS — My Attendance & Payslips
 * Employee self-service: attendance history, payslips, leave balance.
 * APIs: /hr/me  /hr/me/payslips  /hr/me/attendance?month=  /hr/me/leave-balance
 */
import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { formatISTTime, formatWeekday, formatDayMonth, todayISTStr } from '../../lib/format';
import { toast } from 'sonner';
import {
  CalendarDays, FileText, Wallet, Clock, CheckCircle2,
  XCircle, MinusCircle, ChevronLeft, ChevronRight, Download, AlertCircle, X,
} from 'lucide-react';
import { useUrlTab } from '../../lib/hooks/useUrlState';
import { TimeInput } from '../../components/TimeInput';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun',
                'Jul','Aug','Sep','Oct','Nov','Dec'];

function monthLabel(m) {
  const [y, mo] = m.split('-');
  return `${MONTHS[parseInt(mo, 10) - 1]} ${y}`;
}

function nowMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function prevMonth(m) {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonth(m) {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const STATUS_STYLE = {
  P:   { icon: CheckCircle2, cls: 'text-emerald-600', label: 'P' },
  WFH: { icon: CheckCircle2, cls: 'text-blue-500',    label: 'WFH' },
  WO:  { icon: MinusCircle,  cls: 'text-slate-400',   label: 'WO' },
  HO:  { icon: MinusCircle,  cls: 'text-sky-400',     label: 'HO' },
  A:   { icon: XCircle,      cls: 'text-rose-500',    label: 'A' },
  H:   { icon: MinusCircle,  cls: 'text-amber-500',   label: 'HD' },
  PL:  { icon: MinusCircle,  cls: 'text-violet-500',  label: 'PL' },
  EL:  { icon: MinusCircle,  cls: 'text-violet-500',  label: 'EL' },
  SL:  { icon: MinusCircle,  cls: 'text-pink-500',    label: 'SL' },
  CL:  { icon: MinusCircle,  cls: 'text-purple-500',  label: 'CL' },
  CO:  { icon: MinusCircle,  cls: 'text-amber-600',   label: 'CO' },
  LWP: { icon: XCircle,      cls: 'text-zinc-600',    label: 'LWP' },
  L:   { icon: MinusCircle,  cls: 'text-violet-500',  label: 'L' },
  default: { icon: MinusCircle, cls: 'text-slate-300', label: '—' },
};

const REG_STATUS_CLR = {
  Pending: 'bg-amber-50 text-amber-700',
  Approved: 'bg-emerald-50 text-emerald-700',
  Rejected: 'bg-rose-50 text-rose-700',
  'Clarification Requested': 'bg-blue-50 text-blue-700',
};

// N2/item-4: modal to request a Missed Punch-In / Missed Punch-Out / full-day
// Absent regularisation for any past day. Shared by MobileESS (My ESS) AND
// MyAttendance (/m/attend) — 30-Sep-2026 Chunk B fix #4.
export function MissedPunchModal({ defaultDate, defaultType, onClose, onSubmit, submitting }) {
  const [date, setDate] = useState(defaultDate);
  const [type, setType] = useState(defaultType || 'missed_punch_in');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [reason, setReason] = useState('');
  // Item 19 (Fixes_Combined): a punch-out after midnight lands on the next
  // calendar day; the hours then run across midnight (09:30 → 01:30 = 16h).
  const [nextDay, setNextDay] = useState(false);
  const isAbsentReg = type === 'absent_regularization';
  const showOut = isAbsentReg || type === 'missed_punch_out';
  const badOrder = showOut && checkIn && checkOut && !nextDay && checkOut < checkIn;
  const canSubmit = date && reason.trim().length >= 5 && !badOrder
    && (isAbsentReg ? (checkIn && checkOut) : (type === 'missed_punch_in' ? checkIn : checkOut));
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" data-testid="m-ess-missed-punch-modal">
      <div className="bg-white w-full rounded-t-2xl p-5 max-h-[85vh] overflow-y-auto" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-base text-slate-800">{isAbsentReg ? 'Regularise Attendance' : 'Request Missed Punch'}</h3>
          <button onClick={onClose} data-testid="m-ess-missed-punch-close"><X size={18} className="text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">Date</label>
            <input type="date" value={date} max={todayISTStr()}
              onChange={(e) => setDate(e.target.value)} style={{ colorScheme: 'light' }}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 bg-white"
              data-testid="m-ess-missed-punch-date" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} style={{ colorScheme: 'light' }}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 bg-white"
              data-testid="m-ess-missed-punch-type">
              <option value="missed_punch_in">Missed Punch-In</option>
              <option value="missed_punch_out">Missed Punch-Out</option>
              <option value="absent_regularization">Regularise an Absent day</option>
            </select>
          </div>
          {(isAbsentReg || type === 'missed_punch_in') && (
            <div>
              <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">Punch-in time</label>
              <TimeInput
                value={checkIn}
                onChange={setCheckIn}
                testid="m-ess-missed-punch-checkin"
                label="Punch-in"
              />
            </div>
          )}
          {(isAbsentReg || type === 'missed_punch_out') && (
            <div>
              <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">Punch-out time</label>
              <TimeInput
                value={checkOut}
                onChange={setCheckOut}
                testid="m-ess-missed-punch-checkout"
                label="Punch-out"
              />
            </div>
          )}
          {showOut && (
            <label className="flex items-start gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={nextDay} onChange={(e) => setNextDay(e.target.checked)}
                className="mt-0.5" data-testid="m-ess-missed-punch-next-day" />
              <span>Punch-out was after midnight (next day)</span>
            </label>
          )}
          {badOrder && (
            <p className="text-xs text-rose-600" data-testid="m-ess-missed-punch-order-error">
              Punch-out is earlier than the punch-in. If the shift ended after midnight, tick the box above.
            </p>
          )}
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">Reason</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder={isAbsentReg ? 'Why should this day be marked present?' : 'Why was the punch missed?'}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 bg-white placeholder:text-slate-400" data-testid="m-ess-missed-punch-reason" />
          </div>
          <button
            onClick={() => onSubmit({ date, type, checkIn, checkOut, reason, punchOutNextDay: nextDay })}
            disabled={submitting || !canSubmit}
            data-testid="m-ess-missed-punch-submit"
            className="w-full py-3 bg-[#3b4872] text-white rounded-lg text-sm font-display font-bold disabled:opacity-40"
          >
            {submitting ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MobileESS() {
  const [tab, setTab] = useUrlTab('tab', 'attendance');
  const [month, setMonth]         = useState(nowMonth());
  const [profile, setProfile]     = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [payslips, setPayslips]   = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [regRequests, setRegRequests] = useState([]);
  const [missedPunch, setMissedPunch] = useState(null); // { date, type } | null
  const [submittingReg, setSubmittingReg] = useState(false);

  useEffect(() => {
    Promise.all([
      apiGet('/hr/me').catch(() => null),
      apiGet('/hr/me/leave-balance').catch(() => ({ balance: [] })),
      apiGet('/hr/me/payslips').catch(() => ({ payslips: [] })),
    ]).then(([me, lb, ps]) => {
      setProfile(me?.employee || null);
      setLeaveBalance(lb?.balance || []);
      setPayslips(ps?.payslips || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!profile) return;
    apiGet(`/hr/me/attendance?month=${month}`)
      .then(r => setAttendance(r?.attendance || []))
      .catch(() => setAttendance([]));
  }, [month, profile]);

  const loadRegRequests = () => {
    if (!profile?.id) return;
    apiGet('/hr/regularization', { employee_id: profile.id })
      .then(r => setRegRequests(r?.requests || []))
      .catch(() => {});
  };
  useEffect(loadRegRequests, [profile]);

  const submitMissedPunch = async ({ date, type, checkIn, checkOut, reason, punchOutNextDay }) => {
    setSubmittingReg(true);
    try {
      const payload = { date, request_type: type, reason, punch_out_next_day: !!punchOutNextDay };
      if (checkIn) payload.check_in = checkIn;
      if (checkOut) payload.check_out = checkOut;
      await apiPost('/hr/me/regularization', payload);
      toast.success('Request submitted for your manager/HR to approve');
      setMissedPunch(null);
      loadRegRequests();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error((typeof detail === 'string' && detail) || detail?.message || 'Failed to submit request');
    }
    setSubmittingReg(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh] text-slate-400 text-sm" data-testid="m-ess-loading">
      Loading your records…
    </div>
  );

  if (!profile) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center" data-testid="m-ess-no-profile">
      <FileText size={36} className="text-slate-300 mb-3" />
      <p className="text-sm text-slate-500 font-display font-bold">Not linked to an employee record</p>
      <p className="text-xs text-slate-400 mt-1">Ask HR to link your login to your employee profile.</p>
    </div>
  );

  // D3: deduplicate by date — keep best row per date based on source priority.
  // Prevents duplicate cards and inflated totals when hr_attendance has 2+ rows
  // for the same (employee, date) (production issue D1).
  const SRC_PRIO = { manual_hr: 0, auto_from_leave: 1, missed_punch: 1, mobile_punch: 2, auto: 3, auto_holiday: 4, auto_weekly_off: 4 };
  const byDate = {};
  attendance.forEach(a => {
    if (!byDate[a.date]) { byDate[a.date] = a; return; }
    const cur = SRC_PRIO[byDate[a.date].source] ?? 99;
    const cand = SRC_PRIO[a.source] ?? 99;
    if (cand < cur || (cand === cur && (a.auto_marked_at || a.updated_at || '') > (byDate[a.date].auto_marked_at || byDate[a.date].updated_at || '')))
      byDate[a.date] = a;
  });
  const dedupedAttendance = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));

  // Totals are computed from the deduplicated set (one entry per date).
  const present = dedupedAttendance.filter(a => new Set(['P', 'WFH']).has(a.status)).length;
  const absent  = dedupedAttendance.filter(a => new Set(['A', 'LWP', 'LOP']).has(a.status)).length;
  const leaves  = dedupedAttendance.filter(a => new Set(['PL', 'EL', 'CL', 'SL', 'ML', 'CO', 'L', 'SPL']).has(a.status)).length;
  const weekOffHoliday = dedupedAttendance.filter(a => new Set(['WO', 'HO']).has(a.status)).length;
  const todayStr = todayISTStr();
  // N2: latest regularization request per date, so a day already Pending/Approved
  // doesn't show a redundant "missed punch" action.
  const regByDate = {};
  regRequests.forEach(r => {
    if (!regByDate[r.date] || (r.created_at || '') > (regByDate[r.date].created_at || '')) regByDate[r.date] = r;
  });
  // D3: leave/off statuses never need a Missed Punch reminder.
  // Includes legacy 'Paid Leave' string from pre-G3 auto_from_leave records.
  const LEAVE_OR_OFF = new Set(['WO', 'HO', 'PL', 'EL', 'CL', 'SL', 'ML', 'CO', 'LWP', 'SPL', 'L', 'Paid Leave']);

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="m-ess-page">
      {/* Header */}
      <div className="bg-[#3b4872] text-white px-4 pt-4 pb-6">
        <p className="text-xs font-display font-bold uppercase tracking-widest text-white/60 mb-1">
          My Profile
        </p>
        <h1 className="font-display font-bold text-xl">{profile.name}</h1>
        <p className="text-sm text-white/70 mt-0.5">
          {profile.designation || profile.role} · {profile.department}
        </p>
        <p className="text-xs text-white/50 mt-0.5">{profile.employee_id || ''}</p>
      </div>

      {/* Tab bar */}
      <div className="flex bg-white border-b border-slate-200 sticky top-0 z-10">
        {[
          { key: 'attendance', label: 'Attendance', icon: CalendarDays },
          { key: 'payslips',   label: 'Payslips',   icon: FileText    },
          { key: 'leave',      label: 'Leave',       icon: Wallet      },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`m-ess-tab-${key}`}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-display font-bold border-b-2 transition-colors ${
              tab === key ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-400'
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* ── Attendance tab ── */}
      {tab === 'attendance' && (
        <div className="px-4 pt-4">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setMonth(prevMonth(month))} className="p-2 rounded-full hover:bg-slate-200" data-testid="m-ess-prev-month">
              <ChevronLeft size={18} className="text-slate-600" />
            </button>
            <span className="font-display font-bold text-slate-800">{monthLabel(month)}</span>
            <button
              onClick={() => setMonth(nextMonth(month))}
              disabled={month >= nowMonth()}
              className="p-2 rounded-full hover:bg-slate-200 disabled:opacity-30"
              data-testid="m-ess-next-month"
            >
              <ChevronRight size={18} className="text-slate-600" />
            </button>
          </div>

          <button
            onClick={() => setMissedPunch({ date: todayStr, type: 'missed_punch_in' })}
            data-testid="m-ess-add-missed-punch-btn"
            className="w-full mb-4 py-2.5 border border-dashed border-slate-300 rounded-xl text-[12px] font-display font-bold text-[#3b4872] flex items-center justify-center gap-1.5 hover:bg-slate-100"
          >
            <AlertCircle size={13} /> Request Missed Punch
          </button>

          {/* Summary chips */}
          <div className="grid grid-cols-4 gap-1.5 mb-4">
            {[
              { label: 'Present', value: present, cls: 'bg-emerald-50 text-emerald-700' },
              { label: 'Absent',  value: absent,  cls: 'bg-rose-50 text-rose-700'     },
              { label: 'Leave',   value: leaves,  cls: 'bg-amber-50 text-amber-700'   },
              { label: 'Week-off / Holiday', value: weekOffHoliday, cls: 'bg-slate-100 text-slate-600', testid: 'm-ess-chip-wo-ho' },
            ].map(({ label, value, cls, testid }) => (
              <div key={label} className={`${cls} rounded-xl py-3 text-center`} data-testid={testid}>
                <div className="font-display font-extrabold text-2xl">{value}</div>
                <div className="text-[9px] font-bold uppercase tracking-wider mt-0.5 leading-tight">{label}</div>
              </div>
            ))}
          </div>

          {/* Day list */}
          {attendance.length === 0
            ? <p className="text-center text-sm text-slate-400 py-8">No records for this month.</p>
            : (
              <div className="space-y-1.5">
                {[...dedupedAttendance].reverse().map(a => {
                  const s = STATUS_STYLE[a.status] || STATUS_STYLE.default;
                  const Icon = s.icon;
                  const isPast = a.date < todayStr;
                  // D3: never show a missed-punch reminder for leave / off days.
                  const isLeaveOrOff = LEAVE_OR_OFF.has(a.status);
                  const missingIn = isPast && !a.check_in && !isLeaveOrOff;
                  const missingOut = isPast && a.check_in && !a.check_out && !isLeaveOrOff;
                  const isAbsent = isPast && a.status === 'A';
                  const reg = regByDate[a.date];
                  return (
                    <div key={a.date}
                      className="bg-white rounded-xl px-4 py-3 border border-slate-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-display font-bold text-[13px] text-slate-800">
                            {formatWeekday(a.date)}, {formatDayMonth(a.date)}
                          </p>
                          {(a.check_in || a.check_out) && (
                            <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                              <Clock size={10} />
                              {a.check_in ? formatISTTime(a.check_in) : '--:--'}
                              {' → '}
                              {a.check_out ? formatISTTime(a.check_out) : '--:--'}
                              {a.hours_worked ? ` · ${parseFloat(a.hours_worked).toFixed(1)}h` : ''}
                            </p>
                          )}
                        </div>
                        <div className={`flex items-center gap-1 ${s.cls}`}>
                          <Icon size={16} />
                          <span className="font-display font-bold text-sm">{a.status || '—'}</span>
                        </div>
                      </div>
                      {(missingIn || missingOut || isAbsent) && (
                        reg ? (
                          <div className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-display font-bold ${REG_STATUS_CLR[reg.status] || 'bg-slate-50 text-slate-600'}`} data-testid={`m-ess-reg-status-${a.date}`}>
                            <AlertCircle size={10} /> {reg.request_type === 'missed_punch_out' ? 'Punch-out' : reg.request_type === 'absent_regularization' ? 'Regularisation' : 'Punch-in'} request: {reg.status}
                          </div>
                        ) : (
                          <div className="mt-2 flex gap-2">
                            {missingIn && (
                              <button
                                onClick={() => setMissedPunch({ date: a.date, type: 'missed_punch_in' })}
                                data-testid={`m-ess-missed-punch-in-${a.date}`}
                                className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg text-[10px] font-display font-bold"
                              >
                                Missed Punch-In
                              </button>
                            )}
                            {missingOut && (
                              <button
                                onClick={() => setMissedPunch({ date: a.date, type: 'missed_punch_out' })}
                                data-testid={`m-ess-missed-punch-out-${a.date}`}
                                className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg text-[10px] font-display font-bold"
                              >
                                Missed Punch-Out
                              </button>
                            )}
                            {isAbsent && (
                              <button
                                onClick={() => setMissedPunch({ date: a.date, type: 'absent_regularization' })}
                                data-testid={`m-ess-regularise-absent-${a.date}`}
                                className="px-2.5 py-1 bg-rose-50 text-rose-700 rounded-lg text-[10px] font-display font-bold"
                              >
                                Regularise
                              </button>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      )}

      {/* ── Payslips tab ── */}
      {tab === 'payslips' && (
        <div className="px-4 pt-4">
          {payslips.length === 0
            ? <p className="text-center text-sm text-slate-400 py-8">No payslips available yet.</p>
            : (
              <div className="space-y-3">
                {payslips.map(p => (
                  <div key={p.id} className="bg-white rounded-xl px-4 py-4 border border-slate-100" data-testid={`m-ess-payslip-${p.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-display font-bold text-slate-800">{p.month_label || p.month}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{p.days_worked} working days</p>
                      </div>
                      <p className="font-display font-extrabold text-lg text-[#3b4872]">
                        ₹{Number(p.net_pay || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                      <div className="text-[11px] text-slate-500 space-y-0.5">
                        <p data-testid={`m-ess-payslip-gross-${p.id}`}>Gross: ₹{Number(p.gross ?? p.gross_pay ?? 0).toLocaleString('en-IN')}</p>
                        <p data-testid={`m-ess-payslip-deductions-${p.id}`}>Deductions: ₹{Number(p.deductions ?? p.total_deductions ?? 0).toLocaleString('en-IN')}</p>
                        {Number(p.advance_recovery_total || 0) > 0 && (
                          <>
                            <p className="text-amber-700" data-testid={`m-ess-payslip-adv-emi-${p.id}`}>Advance EMI: ₹{Number(p.advance_recovery_total).toLocaleString('en-IN')}</p>
                            <p data-testid={`m-ess-payslip-adv-balance-${p.id}`}>Advance balance: ₹{Number(p.advance_outstanding_after || 0).toLocaleString('en-IN')}</p>
                          </>
                        )}
                      </div>
                      {p.pdf_url && (
                        <a href={p.pdf_url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3b4872] text-white rounded-lg text-[11px] font-display font-bold"
                          data-testid={`m-ess-payslip-dl-${p.id}`}>
                          <Download size={12} /> Download
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {/* ── Leave balance tab ── */}
      {tab === 'leave' && (
        <div className="px-4 pt-4">
          {leaveBalance.length === 0
            ? <p className="text-center text-sm text-slate-400 py-8">No leave balance data.</p>
            : (
              <div className="space-y-3">
                {/* EL first */}
                {(() => {
                  const elBal = leaveBalance.find(lb => lb.leave_type === 'EL');
                  const plBal = leaveBalance.find(lb => lb.leave_type === 'PL');
                  const otherBals = leaveBalance.filter(lb => lb.leave_type !== 'EL' && lb.leave_type !== 'PL' && lb.leave_type !== 'LWP');
                  return (
                    <>
                      {elBal && (
                        <div className="bg-white rounded-xl px-4 py-4 border border-emerald-200 bg-emerald-50/30" data-testid="m-ess-leave-EL">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-display font-bold text-slate-800">EL <span className="text-[10px] font-normal text-slate-400">Earned Leave</span></p>
                            <p className="font-display font-extrabold text-xl text-emerald-600">
                              {elBal.available ?? elBal.remaining ?? 0}
                              <span className="text-xs font-normal text-slate-400"> available</span>
                            </p>
                          </div>
                          <p className="text-[11px] text-slate-400">
                            Accrued {elBal.quota ?? 0} · Used {elBal.used ?? 0}
                            {elBal.pending > 0 && <span className="text-amber-600"> · Pending {elBal.pending}</span>}
                          </p>
                        </div>
                      )}
                      {plBal && (
                        <div className="bg-white rounded-xl px-4 py-4 border border-violet-200 bg-violet-50/30" data-testid="m-ess-leave-PL">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-display font-bold text-slate-800">PL <span className="text-[10px] font-normal text-slate-400">Privilege Leave</span></p>
                            <p className="font-display font-extrabold text-xl text-violet-600">
                              {plBal.available ?? plBal.remaining ?? 0}
                              <span className="text-xs font-normal text-slate-400"> available</span>
                            </p>
                          </div>
                          <p className="text-[11px] text-slate-400">
                            Accrued {plBal.quota ?? 0} · Used {plBal.used ?? 0}
                            {plBal.pending > 0 && <span className="text-amber-600"> · Pending {plBal.pending}</span>}
                          </p>
                        </div>
                      )}
                      {otherBals.map(lb => (
                        <div key={lb.leave_type} className="bg-white rounded-xl px-4 py-4 border border-slate-100" data-testid={`m-ess-leave-${lb.leave_type}`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-display font-bold text-slate-800">{lb.leave_type}</p>
                            <p className="font-display font-extrabold text-xl text-[#3b4872]">
                              {lb.available ?? lb.remaining ?? 0}
                              <span className="text-xs font-normal text-slate-400"> available</span>
                            </p>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#3b4872] rounded-full"
                              style={{ width: lb.quota ? `${Math.min(100, ((lb.available ?? 0) / lb.quota) * 100)}%` : '0%' }} />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">
                            Accrued {lb.quota ?? 0} · Used {lb.used ?? 0}
                            {lb.pending > 0 && <span className="text-amber-600"> · Pending {lb.pending}</span>}
                          </p>
                        </div>
                      ))}
                      <div className="bg-white rounded-xl px-4 py-4 border border-slate-100" data-testid="m-ess-leave-LWP">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-display font-bold text-slate-800">LWP <span className="text-[10px] font-normal text-slate-400">Leave Without Pay</span></p>
                          <p className="font-display font-extrabold text-xl text-zinc-500">Unlimited</p>
                        </div>
                        <p className="text-[11px] text-slate-400">When EL &amp; PL are exhausted · deducted from salary</p>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
        </div>
      )}

      {missedPunch && (
        <MissedPunchModal
          defaultDate={missedPunch.date}
          defaultType={missedPunch.type}
          submitting={submittingReg}
          onClose={() => setMissedPunch(null)}
          onSubmit={submitMissedPunch}
        />
      )}
    </div>
  );
}
